import React, { useEffect, useState, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  CircularProgress,
  Button,
} from "@material-ui/core";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import DescriptionIcon from "@material-ui/icons/Description";
import AutoAwesomeIcon from "@material-ui/icons/EmojiObjects";
import { useVoice } from "../../context/Voice/VoiceContext";
import api from "../../services/api";
import * as apiVoice from "../../services/apiVoice";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: "8px 10px",
    marginBottom: 8,
  },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  meta: { fontSize: 11, color: theme.palette.text.secondary },
  actions: { display: "flex", gap: 4, marginTop: 4 },
  empty: { fontSize: 12, color: theme.palette.text.secondary },
  block: { marginBottom: 12 },
  blockLabel: { fontSize: 12, color: theme.palette.text.secondary, marginBottom: 2 },
}));

const VoiceCallHistory = ({ contactId }) => {
  const classes = useStyles();
  const { status } = useVoice();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // { call, transcript, analysis }
  const [busy, setBusy] = useState(false);
  const audioRef = useRef(null);

  // Stop playback + free the blob URL on unmount.
  useEffect(
    () => () => {
      if (audioRef.current) {
        audioRef.current.audio.pause();
        URL.revokeObjectURL(audioRef.current.url);
      }
    },
    []
  );

  const load = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const data = await apiVoice.listCallsByContact(contactId);
      setCalls(Array.isArray(data) ? data : []);
    } catch (err) {
      /* 403 when disabled */
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    if (status && status.enabled) load();
  }, [status, load]);

  if (!status || !status.enabled) return null;

  const listen = async (recordingId) => {
    try {
      // Stop + free any previously-playing recording first.
      if (audioRef.current) {
        audioRef.current.audio.pause();
        URL.revokeObjectURL(audioRef.current.url);
        audioRef.current = null;
      }
      const resp = await api.get(apiVoice.recordingUrl(recordingId), {
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const audio = new Audio(url);
      audioRef.current = { audio, url };
      audio.onended = () => {
        URL.revokeObjectURL(url);
        if (audioRef.current && audioRef.current.url === url) audioRef.current = null;
      };
      await audio.play().catch((e) => {
        toastError(e);
      });
    } catch (err) {
      toastError(err);
    }
  };

  const openDetail = async (call) => {
    setBusy(true);
    try {
      const full = await apiVoice.getCall(call.id);
      let transcript = full.transcript;
      if (!transcript || transcript.status !== "done") {
        try {
          const t = await apiVoice.getCallTranscript(call.id);
          if (t && t.status === "done") transcript = t;
        } catch (e) {
          /* 202/404 — not ready */
        }
      }
      setDetail({ call: full.call, recording: full.recording, transcript, analysis: full.analysis });
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const runAnalyze = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      const analysis = await apiVoice.analyzeCall(detail.call.id);
      setDetail((d) => ({ ...d, analysis }));
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const fmtDur = (s) =>
    `${Math.floor((s || 0) / 60)}:${String((s || 0) % 60).padStart(2, "0")}`;

  if (loading && !calls.length) return <CircularProgress size={18} />;
  if (!calls.length) return <div className={classes.empty}>{i18n.t("voice.calls.empty")}</div>;

  return (
    <div>
      {calls.map((c) => (
        <div key={c.id} className={classes.card}>
          <div className={classes.row}>
            <span style={{ fontWeight: 500, fontSize: 13 }}>
              {c.direction === "inbound" ? "↙" : "↗"} {fmtDur(c.durationSec)}
            </span>
            <span className={classes.meta}>
              {new Date(c.startedAt || c.createdAt).toLocaleString("he-IL")}
            </span>
          </div>
          <div className={classes.meta}>
            {c.user ? `${i18n.t("voice.calls.agent")}: ${c.user.name} · ` : ""}
            {c.status}
            {c.outcome ? ` · ${i18n.t(`voice.outcome.${c.outcome === "follow_up" ? "followUp" : c.outcome === "no_answer" ? "noAnswer" : c.outcome}`)}` : ""}
          </div>
          <div className={classes.actions}>
            <Tooltip title={i18n.t("voice.calls.transcript")}>
              <IconButton size="small" onClick={() => openDetail(c)}>
                <DescriptionIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      ))}

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{i18n.t("voice.analysis.title")}</DialogTitle>
        <DialogContent dividers>
          {busy && <CircularProgress size={20} />}
          {detail && detail.recording && (
            <div className={classes.block}>
              <Button
                size="small"
                startIcon={<PlayArrowIcon />}
                onClick={() => listen(detail.recording.id)}
              >
                {i18n.t("voice.calls.listen")}
              </Button>
            </div>
          )}
          {detail && detail.transcript && detail.transcript.status === "done" ? (
            <div className={classes.block}>
              <div className={classes.blockLabel}>{i18n.t("voice.calls.transcript")}</div>
              <div style={{ fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                {detail.transcript.text}
              </div>
            </div>
          ) : (
            <div className={classes.empty}>{i18n.t("voice.calls.transcribing")}</div>
          )}

          {detail && detail.analysis ? (
            <div>
              <div className={classes.block}>
                <div className={classes.blockLabel}>{i18n.t("voice.analysis.summary")}</div>
                <div style={{ fontSize: 13 }}>{detail.analysis.summary}</div>
              </div>
              <div className={classes.block}>
                {detail.analysis.intent && (
                  <Chip size="small" label={`${i18n.t("voice.analysis.intent")}: ${detail.analysis.intent}`} style={{ marginInlineEnd: 6 }} />
                )}
                {detail.analysis.sentiment && (
                  <Chip size="small" label={`${i18n.t("voice.analysis.sentiment")}: ${detail.analysis.sentiment}`} />
                )}
              </div>
              {detail.analysis.suggestedReply && (
                <div className={classes.block}>
                  <div className={classes.blockLabel}>{i18n.t("voice.analysis.suggestedReply")}</div>
                  <div style={{ fontSize: 13 }}>{detail.analysis.suggestedReply}</div>
                </div>
              )}
            </div>
          ) : (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutoAwesomeIcon />}
              onClick={runAnalyze}
              disabled={busy || !detail || !detail.transcript || detail.transcript.status !== "done"}
            >
              {i18n.t("voice.calls.analyze")}
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VoiceCallHistory;
